// Cross-package schemas for the snapshot `meta.json` document.
//
// `meta.json` is the small descriptor next to each `layer-N.png` PNG under
// `{room}/sessions/{sessionId}/snapshot-{seq}/` in S3 / MinIO. The server
// writes it via `AwsS3SnapshotPublisher`; the client reads it via
// `SnapshotLoader` after fetching the presigned GET URL from
// `room.welcome.snapshot.metaUrl` / `room.resume-ok.snapshot.metaUrl`.
//
// The schema lives in `@oekaki/protocol` (not in either of its consumers)
// because both server-side serialization and client-side parsing must use
// the **exact same** field names + types — a drift between writer and
// reader is a silent snapshot corruption bug, indistinguishable from a
// data loss. Re-using a Zod schema sidesteps that.
//
// (architecture.md §14 / §15, docs/_wip/layer-background-separation-2026-05-17.md
// §「設計 (採用案)」「影響範囲」)
import { z } from 'zod';
/**
 * Per-layer descriptor inside `meta.json.layers[]`.
 *
 * MVP only emits `blendMode: 'normal'`; β Modern Layout will extend the
 * enum as layer blend modes are designed. The shape mirrors the older
 * inline interfaces in `server/.../AwsS3SnapshotPublisher.ts` and
 * `web/.../SnapshotLoader.ts` that this schema now subsumes.
 *
 * Phase 2 incremental snapshot (`docs/_wip/phase2-incremental-snapshot.md`)
 * adds two optional fields. Both are `optional()` for backward compat —
 * legacy meta.json written before Phase 2 simply omits them, and the
 * server treats their absence as "always re-encode" (= skip 判定不成立):
 *
 * - `url`: the layer PNG URL. After Phase 2, an unchanged layer's `url`
 *   points at the **previous** snapshot dir's PNG (= the layer is reused
 *   without re-encoding). Before Phase 2, every layer's PNG always lived
 *   under the current snapshot dir so `url` was redundant and unstored.
 * - `lastEventSeq`: the seq of the last event that affected this layer.
 *   The next snapshot compares it against the events buffer's per-layer
 *   max seq to decide skip vs re-encode.
 */
export const snapshotLayerMetaSchema = z.object({
    id: z.number().int().nonnegative(),
    /** Layer opacity 0..1. MVP always 1; β layer panel will vary it. */
    opacity: z.number().min(0).max(1),
    blendMode: z.literal('normal'),
    /** Phase 2: previous-snapshot URL for skipped (unchanged) layers. */
    url: z.string().optional(),
    /** Phase 2: seq of the last event that touched this layer. */
    lastEventSeq: z.number().int().nonnegative().optional(),
});
/**
 * Canvas background descriptor carried in `meta.json` (and in-engine).
 *
 * The "canvas background" is the color composited **under** every layer
 * at display / export time. It is intentionally NOT a layer pixel — the
 * α 公開時に発覚した「キャプ画像で消しゴム跡が黒く焼き付く」/「スポイトで
 * 消しゴム跡を吸うと黒」 symptoms (docs/_wip/layer-background-separation-
 * 2026-05-17.md) were caused by treating layer 0 as the background; we
 * now keep them strictly separate.
 *
 * `rgb` is a 24-bit color (`0..0xFFFFFF`). `alpha` is `0..255`. The MVP
 * default is opaque white (`0xFFFFFF, 255`); future room settings may
 * let owners pick the canvas color.
 */
export const snapshotBackgroundSchema = z.object({
    rgb: z.number().int().min(0).max(0xffffff),
    alpha: z.number().int().min(0).max(255),
});
/**
 * Default canvas background — opaque white. Mirrors the legacy startup
 * state where the canvas was painted opaque white at room init. Exposed
 * as a const so client + server agree on the fallback when `meta.json`
 * omits the `background` field (= snapshots written before this schema
 * was deployed). The `as const` type makes it usable as a Zod default.
 */
export const DEFAULT_SNAPSHOT_BACKGROUND = {
    rgb: 0xffffff,
    alpha: 255,
};
/**
 * The full `meta.json` document.
 *
 * Forward-compat policy: `background` is **optional with a default** so
 * snapshots produced before this schema was deployed (= no `background`
 * field) keep loading; the loader transparently fills in the default
 * (= the legacy bg-white assumption). Existing snapshots therefore don't
 * need to be re-written. New snapshots always carry `background`.
 *
 * Backward-compat policy: this schema is `loose()` so any future field
 * (e.g. canvas-level blend mode, palette swatch list) round-trips through
 * older readers without rejection.
 */
export const snapshotMetaSchema = z
    .object({
    /**
     * Phase 2 incremental snapshot (`docs/_wip/phase2-incremental-snapshot.md`)
     * stamps `metaVersion: 'v1'` so future protocol-breaking variants
     * (= e.g. (B) layer-別 atomic) can be discriminated. Optional for
     * backward compat: legacy meta.json written before Phase 2 simply
     * omits the field. Server-side `parseSnapshotMeta` uses absence (+
     * absence of every `layers[].lastEventSeq`) as the legacy signal.
     */
    metaVersion: z.literal('v1').optional(),
    /**
     * Phase 2: top-level snapshot revision seq. Same value as `lastSeq`
     * in MVP, but kept separate so a future `(B)` layer-別 atomic variant
     * can decouple "meta revision" from "max event seq across all layers".
     * Optional for backward compat.
     */
    seq: z.number().int().nonnegative().optional(),
    lastSeq: z.number().int().nonnegative(),
    layers: z.array(snapshotLayerMetaSchema),
    background: snapshotBackgroundSchema.optional().default(DEFAULT_SNAPSHOT_BACKGROUND),
})
    .loose();
//# sourceMappingURL=snapshot.js.map